<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { IonButton, IonContent, IonIcon, IonInput, IonSpinner } from '@ionic/vue';
import { closeOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import ModalOne from '@/shared/components/ui/ModalOne.vue';
import TopBar from '@/shared/components/TopBar.vue';
import BackButton from '@/shared/components/BackButton.vue';
import useSelfOrdering from '@/modules/self-ordering/components/useSelfOrdering';
import useTableSpotLocation from '@/modules/self-ordering/composables/useTableSpotLocation';
import { useSharedStore } from '@/stores/shared/sharedStore';

// Init
const { t } = useI18n();
const locationSearch = ref('');
const route = useRoute();
const router = useRouter();
const sharedStore = useSharedStore();
const selfOrderingSpotId = route.params.id.toString();
const hasOpenedFirstCategory = ref(false);
sharedStore.currentSelfOrderingSpotId = selfOrderingSpotId;

const { clearLocationAndContinue, closeLocationModal, getProductCategories, getTableSpotByQRCode, locationModalOpen, tableSpot } = useSelfOrdering(selfOrderingSpotId);
const { getLocation, saveLocation } = useTableSpotLocation();
const { isFetchingProductCategories, tableSpotProductCategories } = getProductCategories(selfOrderingSpotId);

const openFirstCategory = () => {
	const category = tableSpotProductCategories.value?.[0];

	if (hasOpenedFirstCategory.value || !category || !tableSpot.value) {
		return;
	}

	if (tableSpot.value.manual_location && !getLocation()) {
		return;
	}

	hasOpenedFirstCategory.value = true;
	router.replace({
		name: 'self-ordering-cart',
		params: { id: category.id },
		query: category.default_currency_id ? { dcid: category.default_currency_id } : {},
	});
};

// selectLocation
const selectLocation = (locationSelected: string) => {
	saveLocation(locationSelected);
	locationModalOpen.value = false;
	openFirstCategory();
};

onMounted(async () => {
	await getTableSpotByQRCode();
});

watch(
	[tableSpotProductCategories, tableSpot, locationModalOpen],
	() => {
		if (!locationModalOpen.value) {
			openFirstCategory();
		}
	},
	{ immediate: true },
);

onBeforeUnmount(() => {
	// Close modal
	document.getElementsByTagName('ion-modal')[0]?.dismiss();
});

// clearSearch
const clearSearch = () => {
	locationSearch.value = '';
};
</script>

<template>
	<ion-content>
		<ModalOne :title="t('self_ordering.what_is_your_location')" :is-open="locationModalOpen">
			<template #content>
				<div class="mt-6 mb-2 font-sourcesanspro-regular text-base2 text-white">
					{{ t('self_ordering.enter_location') }}
				</div>
				<ion-input v-model="locationSearch" class="rounded-full bg-red font-sourcesanspro-semibold text-base2 text-white location-searchbar bg-black/10" shape="round" :placeholder="`${t('self_ordering.enter_manual_location_type')} ${tableSpot?.manual_location_type}`" :debounce="500" @ion-cancel="clearSearch()"></ion-input>
				<div class="mb-4">
					<!-- <span v-show="!tableSpot?.manual_location_values.split(',').includes(locationSearch)" class="text-xs">The provided location is unknown</span> -->
				</div>
				<div class="flex flex-col mb-2 max-h-[350px] overflow-scroll">
					<div class="mb-4">
						<span class="font-sourcesanspro-semibold text-base2 text-white">{{ t('self_ordering.suggestions') }}</span>
					</div>
					<div
						v-for="(location, idx) in tableSpot?.manual_location_values.split(',').filter((location: string) => location.toLowerCase().includes(locationSearch.toLowerCase()))"
						:key="idx"
						class="flex flex-col items-start justify-center font-sourcesanspro-regular text-m2 text-white cursor-pointer"
						:class="[
							{
								'bg-[#514f5a]': getLocation() === location,
							},
						]"
						@click="selectLocation(location)">
						<div class="flex p-2 items-center">
							<div v-dompurify-html="location.replaceAll(locationSearch, `<span class='text-white'><b>${locationSearch}</b></span>`)"></div>
						</div>
					</div>
				</div>
				<ion-button @click="selectLocation(locationSearch)" :disabled="!tableSpot?.manual_location_values.split(',').includes(locationSearch)" expand="full" shape="round">
					{{ t('self_ordering.continue') }}
				</ion-button>
			</template>
			<template #actions>
				<div class="flex items-center justify-center">
					<div class="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-white">
						<ion-icon class="text-[38px] text-black cursor-pointer" :icon="closeOutline" @click="closeLocationModal()"></ion-icon>
					</div>
				</div>
			</template>
		</ModalOne>
		<div class="flex flex-col justify-center px-[34px]">
			<TopBar>
				<template #left>
					<BackButton :onClick="clearLocationAndContinue" />
				</template>
			</TopBar>

			<div class="flex flex-col items-center justify-center mb-6">
				<h2 class="font-sourcesanspro-semibold text-m2">{{ t('self_ordering.page_title') }}</h2>
				<span v-if="tableSpot && tableSpot.manual_location" class="font-sourcesanspro-regular text-l2 text-white/60">{{ getLocation() }}</span>
				<span v-if="tableSpot && !tableSpot.manual_location" class="font-sourcesanspro-regular text-l2 text-white/60">{{ tableSpot?.friendly_id }}</span>
			</div>

			<div v-if="isFetchingProductCategories || !tableSpotProductCategories" class="flex items-center justify-center mb-4">
				<ion-spinner name="dots"></ion-spinner>
			</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped></style>
